/**
 * This file is for the code related to the Vaadin router.
 */

import type {Context, Route} from '@vaadin/router';

import {
  getAllParts1,
  getAllParts2,
  landUrlFragment,
  legendUrlFragment,
  overlayDirectUrlFragment,
  recordedTourUrlFragment,
  routeCategoriesUrlFragment,
  routeUrlFragment,
  seasonsUrlFragment,
  segmentUrlFragment,
  slowupLegendUrlFragment,
  tourUrlFragment,
  toursUrlFragment,
} from './urlHandling/urlParams.ts';
import {
  getLayersForLayertree,
  hasFilterUrlParam,
} from './urlHandling/urlSearchParams.ts';
import {
  disableKvp$,
  modal$,
  season$,
  stpShortTitles$,
  updateChosenFilters$,
} from './stores/store-main.ts';
import type {NormalizeURLResult} from './urlHandling/urlNormalizer.ts';
import {noHTML, normalizeURL} from './urlHandling/urlNormalizer.ts';
import type {LanguageCode} from './i18n.ts';
import {languageCodes} from './i18n.ts';
import {
  findTopicTabFromHash,
  fetchStaticPageOrTopic,
} from './apis/api-static-pages-and-topics.ts';
import {resolveDeployPrefix} from './initialization/deployPrefixResolver.ts';
import {landCodeToApiCode} from './layers/layerHelpers.ts';
import {defaultLanguage} from './unspecific/languageUtils.ts';

import {
  bgLayer$,
  visibleAdditionalRouteLayers$,
  visibleLayers$,
} from './stores/store-map.ts';
import {monitorLoadingError} from './initialization/obsoleteApi.ts';

const deployPrefix = resolveDeployPrefix();

const langPart = `:lang(${languageCodes.join('|')})`;
const landPart = `:land(${getAllParts2(landUrlFragment).join('|')})`;
const categoryPart = `:category(${getAllParts2(routeCategoriesUrlFragment).join(
  '|',
)})`;
const seasonPart = `:season(${getAllParts2(seasonsUrlFragment).join('|')})`;
const routePart = `(${getAllParts1(routeUrlFragment).join(
  '|',
)})-:routeNumber(\\d+)`;
const segmentPart = `(${getAllParts1(segmentUrlFragment).join(
  '|',
)})-:segmentNumber(\\d+)`;
const layerPart = `:layer(${getAllParts2(overlayDirectUrlFragment).join(
  '|',
)})-:layerId(\\d+)`;
const layerPartWithTitle = `:layer(${getAllParts2(
  overlayDirectUrlFragment,
).join('|')})-:layerId(\\d+)-(.*)`;
const legendPart = `:legend(${getAllParts1(legendUrlFragment).join('|')})`;
const slowupLegendPart = `:slowupLegend(${getAllParts1(
  slowupLegendUrlFragment,
).join('|')})`;
const toursPart = `:toursPart(${getAllParts1(toursUrlFragment).join('|')})`;
const tourPart = `:tourPart(${getAllParts1(tourUrlFragment).join('|')})`;
const recordedTourPart = `:recordedTourPart(${getAllParts1(
  recordedTourUrlFragment,
).join('|')})`;
const defaultSeasonCode = season$.value;
let isFirstRender = true;
window.addEventListener('vaadin-router-location-changed', (event) => {
  // close modal on page change
  if (!isFirstRender) modal$.next(undefined);
  isFirstRender = false;
  const hasFilter = hasFilterUrlParam();
  updateChosenFilters$.next(hasFilter);
});

function getNormalizedPathResult(pathname: string): NormalizeURLResult {
  // We can't use document.location in the action because it contains the previous URL.
  const url = new URL(pathname, document.location.toString());
  return normalizeURL(
    url.toString(),
    defaultLanguage,
    defaultSeasonCode,
    deployPrefix,
  );
}

const setLoginSearchParams = (oldContext: Context, newContext: Context) => {
  const params = new URLSearchParams(oldContext.search);
  const newParams = new URLSearchParams(newContext.search);
  const {loginEnabled} = Object.fromEntries(params);
  if (loginEnabled === 'true')
    newContext.search = !newParams.entries.length
      ? '?loginEnabled=true'
      : '&loginEnabled=true';
};

const directOverlayLinkAction = async (context: Context) => {
  // iframe check
  if (context.search?.includes('embed')) return iframeAction(context);

  const {bag} = getNormalizedPathResult(context.pathname);
  const {PageMap} = await monitorLoadingError(import('./pages/page-map.ts'));
  const page = new PageMap();
  page.keepLayers = context.search?.includes('keepLayers');
  if (bag.overlayType !== 'travelreport') {
    page.layerToHighlight = {
      id: bag.overlayContentId,
      layerName: bag.overlayType,
    };
  }
  page.allowZoomOnPOI(true);
  return page;
};

const iframeAction = async (context: Context) => {
  const url = new URL(context.pathname, document.location.toString());
  url.search = context.search;
  const {bag, kind} = normalizeURL(
    url.toString(),
    defaultLanguage,
    defaultSeasonCode,
    deployPrefix,
  );
  const {PageMapEmbed} = await monitorLoadingError(
    import('./pages/page-map-embed.ts'),
  );
  const page = new PageMapEmbed();
  if (context.search?.includes('noNavigation')) page.noNavigation = true;
  if (bag.embed === 'simple') page.hideHeaderFooter = true;
  visibleLayers$.next(getLayersForLayertree(url.searchParams));

  // check for tour and recorded track
  if (bag.tourId) {
    page.tourId = bag.tourId;
    page.tourType = kind === 'recordedtrack' ? 'recordedtrack' : 'normal';
  }

  // check for overlay direct links
  if (
    bag.overlayType !== 'travelreport' &&
    bag.overlayType &&
    bag.overlayContentId
  ) {
    page.layerToHighlight = {
      id: bag.overlayContentId,
      layerName: bag.overlayType,
    };
  }

  // check for route and segment
  if (bag.routeNumber) {
    const land = landCodeToApiCode[bag.land];
    page.layerToHighlight = {
      routeNumber: bag.routeNumber,
      layerName: land,
    };
    if (bag.segmentNumber)
      page.layerToHighlight.segmentNumber = bag.segmentNumber;
  }

  if (bag.neutralMapParams?.crosshair)
    page.crosshair = bag.neutralMapParams.crosshair;

  if (bag.neutralMapParams) {
    visibleAdditionalRouteLayers$.next({
      detours: bag.neutralMapParams.detours,
      photos: bag.neutralMapParams.photos,
      logos: bag.neutralMapParams.logos,
      shooting: bag.neutralMapParams.shooting,
    });
  }
  return page;
};

const routeSegmentAction = async (context: Context) => {
  const search = new URLSearchParams(context.search);
  // iframe check
  if (search.has('embed')) {
    return iframeAction(context);
  }

  const {bag} = getNormalizedPathResult(context.pathname);
  disableKvp$.next(true);
  season$.next(bag.season);
  const {PageSegment} = await monitorLoadingError(
    import('./pages/page-segment.ts'),
  );
  const page = new PageSegment();
  page.land = bag.land;
  page.routeNumber = bag.routeNumber;
  page.segmentNumber = bag.segmentNumber || 0;
  page.scrollContainer = document.querySelector<HTMLElement>('#main');
  const noZoom = search.has('noZoom');
  search.delete('noZoom');
  const noScroll = search.has('noScroll');
  search.delete('noScroll');
  const focus = search.get('focus') === 'map';
  page.scrollToTitle = !noZoom && !noScroll && !focus;
  page.mapVisible = noZoom || noScroll || focus;
  page.fitMap = !noZoom || focus;
  page.resetLayers = !search.has('keepLayers');
  search.delete('keepLayers');
  context.search = search.size > 0 ? `?${search.toString()}` : '';
  return page;
};

// See https://docs.google.com/spreadsheets/d/1kNGTIaP6kdcXey3c5YAFFJoAYh7D_-dryZWJ3gtXQRI/edit#gid=1368060383
export const routes: Route[] = [
  {
    path: `${deployPrefix}/${langPart}/${seasonPart}`,
    action: async (context) => {
      const {bag} = getNormalizedPathResult(context.pathname);
      season$.next(bag.season);
      const {PageLandList} = await monitorLoadingError(
        import('./pages/page-land-list.ts'),
      );
      return new PageLandList();
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${landPart}`,
    action: async (context) => {
      const {bag} = getNormalizedPathResult(context.pathname);
      season$.next(bag.season);
      const {PageLandList} = await monitorLoadingError(
        import('./pages/page-land-list.ts'),
      );
      const page = new PageLandList();
      page.land = bag.land;
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${landPart}/${categoryPart}`,
    action: async (context) => {
      const {bag} = getNormalizedPathResult(context.pathname);
      season$.next(bag.season);
      const {PageLandList} = await monitorLoadingError(
        import('./pages/page-land-list.ts'),
      );
      const page = new PageLandList();
      page.land = bag.land;
      page.category = bag.routeCategory;
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${landPart}/${routePart}`,
    action: routeSegmentAction,
  },
  {
    path: `${deployPrefix}/${langPart}/${landPart}/${routePart}/${segmentPart}`,
    action: routeSegmentAction,
  },
  {
    path: `${deployPrefix}/${langPart}/map`,
    action: async (context, commands) => {
      // iframe check
      if (context.search?.includes('embed')) return iframeAction(context);

      const {PageMap} = await monitorLoadingError(
        import('./pages/page-map.ts'),
      );
      const url = new URL(context.pathname, document.location.toString());
      url.search = context.search;
      const {bag} = normalizeURL(
        url.toString(),
        defaultLanguage,
        defaultSeasonCode,
        deployPrefix,
      );
      const page = new PageMap();

      page.fitBbox = bag.neutralMapParams.fitBbox;
      page.keepLayers = bag.neutralMapParams.keepLayers;
      page.crosshair = bag.neutralMapParams.crosshair;
      page.resolution = bag.neutralMapParams.resolution;
      disableKvp$.next(false);
      if (bag.season) season$.next(bag.season);
      if (!page.keepLayers)
        visibleLayers$.next(getLayersForLayertree(url.searchParams));
      if (bag.land && (bag.routeNumber || bag.segmentNumber)) {
        page.layerToHighlight = {
          routeNumber: bag.routeNumber,
          segmentNumber: bag.segmentNumber,
          layerName: landCodeToApiCode[bag.land],
        };
      }
      bgLayer$.next(
        bag.neutralMapParams.bgLayer ? bag.neutralMapParams.bgLayer : 'pk',
      );
      visibleAdditionalRouteLayers$.next({
        detours: bag.neutralMapParams.detours,
        photos: bag.neutralMapParams.photos,
        logos: bag.neutralMapParams.logos,
        shooting: bag.neutralMapParams.shooting,
      });
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${toursPart}`,
    action: async () => {
      const {PageMapTours} = await monitorLoadingError(
        import('./pages/page-map-tours.ts'),
      );
      const page = new PageMapTours();
      page.scrollContainer = document.querySelector<HTMLElement>('#main');
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${tourPart}`,
    action: async (context) => {
      const {PageMapTour: PageMapTourView} = await monitorLoadingError(
        import('./pages/page-map-tour.ts'),
      );
      const page = new PageMapTourView();
      page.type = 'normal';
      page.scrollContainer = document.querySelector<HTMLElement>('#main');
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${tourPart}/:id(\\d+)`,
    action: async (context) => {
      // iframe check
      if (context.search?.includes('embed')) return iframeAction(context);
      const oldContext = {...context};

      const {bag} = getNormalizedPathResult(context.pathname);

      const {PageMapTour: PageMapTourView} = await monitorLoadingError(
        import('./pages/page-map-tour.ts'),
      );
      const poiId = context.search.match(/poiId=(\d+)/);
      const noZoom = context.search?.includes('noZoom');
      const page = new PageMapTourView();
      page.fitMap = !noZoom;
      page.tourId = bag.tourId;
      page.type = 'normal';
      page.activePoiId = poiId ? parseInt(poiId[1]) : undefined;
      page.scrollContainer = document.querySelector<HTMLElement>('#main');
      setLoginSearchParams(oldContext, context);
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${recordedTourPart}/:id(\\d+)`,
    action: async (context) => {
      // iframe check
      if (context.search?.includes('embed')) return iframeAction(context);
      const oldContext = {...context};

      const {bag} = getNormalizedPathResult(context.pathname);
      const {PageMapRecordedTour} = await monitorLoadingError(
        import('./pages/page-map-recordedtour.ts'),
      );
      const noZoom = context.search?.includes('noZoom');
      const page = new PageMapRecordedTour();
      page.fitMap = !noZoom;
      page.tourId = bag.tourId;
      page.type = 'recordedtrack';
      page.scrollContainer = document.querySelector<HTMLElement>('#main');
      setLoginSearchParams(oldContext, context);
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${layerPart}`,
    action: directOverlayLinkAction,
  },
  {
    path: `${deployPrefix}/${langPart}/${layerPartWithTitle}`,
    action: directOverlayLinkAction,
  },
  {
    path: `${deployPrefix}/${langPart}/${landPart}/${layerPart}`,
    action: async (context: Context) => {
      const {bag} = getNormalizedPathResult(context.pathname);
      season$.next(bag.season);
      const {PageLandList} = await monitorLoadingError(
        import('./pages/page-land-list.ts'),
      );
      const page = new PageLandList();
      page.land = bag.land;
      page.layerToShow = {id: bag.overlayContentId, layerName: bag.overlayType};
      return page;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${legendPart}`,
    action: async (context: Context) => {
      const {PageLegend} = await monitorLoadingError(
        import('./pages/page-legend.ts'),
      );
      const element = new PageLegend();
      element.kind = 'legend';
      return element;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/${slowupLegendPart}`,
    action: async (context: Context) => {
      const {PageLegend} = await monitorLoadingError(
        import('./pages/page-legend.ts'),
      );
      const element = new PageLegend();
      element.kind = 'slowup-legend';
      return element;
    },
  },
  {
    path: `${deployPrefix}/${langPart}/feedback(\\.html)*`,
    action: async (context: Context) => {
      if (context.pathname.endsWith('.html'))
        location.pathname = noHTML(context.pathname);
      const {PageFeedback} = await monitorLoadingError(
        import('./pages/page-feedback.ts'),
      );
      return new PageFeedback();
    },
  },
  {
    path: `${deployPrefix}/${langPart}/login`,
    action: async () => {
      const {PageLogin} = await monitorLoadingError(
        import('./pages/page-login.ts'),
      );
      return new PageLogin();
    },
  },
  // all non-wildcards should be added above
  {
    path: `${deployPrefix}/${langPart}/:key(.+)`,
    action: async (context: Context) => {
      // The topics and static pages URLs are not known in advance. As a consequence
      // we tentatively fetch them in this fallback case.
      const lang = context.params['lang'] as LanguageCode;
      const key = context.params['key'] as string;
      try {
        const spt = await fetchStaticPageOrTopic(key, lang);
        stpShortTitles$.next(spt.titleShort);
        const {PageStaticOrTopic} = await monitorLoadingError(
          import('./pages/page-static-or-topic.ts'),
        );
        const sptElement = new PageStaticOrTopic();
        sptElement.spt = spt;

        switch (spt.kind) {
          case 'static_page': {
            sptElement.selectedTab = 0;
            break;
          }
          case 'topic': {
            const hashAndTab = findTopicTabFromHash(spt, context.hash, lang);
            if (hashAndTab) {
              sptElement.selectedTab = hashAndTab.selectedTab;
              context.hash = hashAndTab.hash;
            } else {
              // unusable topic, we just select the first tab
              sptElement.selectedTab = 0;
            }
          }
        }
        return sptElement;
      } catch {
        const {PageNotFound} = await monitorLoadingError(
          import('./pages/page-not-found.ts'),
        );
        return new PageNotFound();
      }
    },
  },
  {
    path: `${deployPrefix}/(.*)`,
    action: async () => {
      const {PageNotFound} = await monitorLoadingError(
        import('./pages/page-not-found.ts'),
      );
      return new PageNotFound();
    },
  },
];
